// =============================================================================
// NewSubbieCompanyScreen — /people/subbies/new
//
// Manager-side form to add a new subcontractor company. These are the
// "ops accounts" — subbie companies you engage operationally (NOT
// suppliers as Xero AP vendors). They populate the Supplier branch
// dropdown in the subbie onboarding wizard (T-154).
//
// Captures everything the wizard + compliance views need:
//   - identity: company_code, legal_name, trading_name, abn
//   - contact:  primary contact name/email/phone
//   - address
//   - insurance expiries (PL, WC, PI) — sets the colour bands on People → Subbies
//   - SWMS on file flag
//   - approved flag (manager attests they've been vetted)
//
// company_code is auto-generated from the trading/legal name if blank.
// =============================================================================

import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Building2, AlertTriangle, ShieldCheck,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Spinner } from "@/components/ui";

const AU_STATES = ["QLD", "NSW", "VIC", "TAS", "SA", "WA", "NT", "ACT"] as const;

function slugifyToCode(s: string): string {
  // Make a stable short company code: uppercase, alnum + dashes, max 20 chars.
  return (s ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 20) || "SUBBIE";
}

export default function NewSubbieCompanyScreen() {
  const user     = useRequireAuth();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const isEdit = !!companyId && companyId !== "new";

  const [legalName,     setLegalName]   = useState("");
  const [tradingName,   setTradingName] = useState("");
  const [companyCode,   setCompanyCode] = useState("");
  const [abn,           setAbn]         = useState("");
  const [contactName,   setContactName] = useState("");
  const [contactEmail,  setContactEmail]= useState("");
  const [contactPhone,  setContactPhone]= useState("");
  const [addr1,         setAddr1]       = useState("");
  const [addr2,         setAddr2]       = useState("");
  const [suburb,        setSuburb]      = useState("");
  const [state,         setState]       = useState<string>("QLD");
  const [postcode,      setPostcode]    = useState("");
  const [plExpiry,      setPlExpiry]    = useState("");
  const [wcExpiry,      setWcExpiry]    = useState("");
  const [piExpiry,      setPiExpiry]    = useState("");
  const [swmsOnFile,    setSwmsOnFile]  = useState(false);
  const [isApproved,    setIsApproved]  = useState(false);
  const [error,         setError]       = useState<string | null>(null);

  // Edit mode — load the existing company then prefill the form once.
  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ["subbie_company_edit", companyId],
    enabled: isEdit,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subcontractor_companies")
        .select("*")
        .eq("subcontractor_company_id", companyId!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });
  useEffect(() => {
    if (!existing) return;
    setLegalName(existing.legal_name ?? "");
    setTradingName(existing.trading_name ?? "");
    setCompanyCode(existing.company_code ?? "");
    setAbn(existing.abn ?? "");
    setContactName(existing.primary_contact_name ?? "");
    setContactEmail(existing.primary_contact_email ?? "");
    setContactPhone(existing.primary_contact_phone ?? "");
    setAddr1(existing.address_line1 ?? "");
    setAddr2(existing.address_line2 ?? "");
    setSuburb(existing.suburb ?? "");
    setState(existing.state ?? "QLD");
    setPostcode(existing.postcode ?? "");
    setPlExpiry(existing.public_liability_expiry ?? "");
    setWcExpiry(existing.workers_comp_expiry ?? "");
    setPiExpiry(existing.professional_indemnity_expiry ?? "");
    setSwmsOnFile(!!existing.swms_on_file);
    setIsApproved(!!existing.is_approved);
  }, [existing]);

  const create = useMutation({
    mutationFn: async () => {
      if (!legalName.trim()) throw new Error("Legal name is required.");

      const code = (companyCode.trim() || slugifyToCode(tradingName || legalName));
      const payload = {
        company_code:           code,
        legal_name:             legalName.trim(),
        trading_name:           tradingName.trim() || null,
        abn:                    abn.trim() || null,
        primary_contact_name:   contactName.trim() || null,
        primary_contact_email:  contactEmail.trim() || null,
        primary_contact_phone:  contactPhone.trim() || null,
        address_line1:          addr1.trim() || null,
        address_line2:          addr2.trim() || null,
        suburb:                 suburb.trim() || null,
        state:                  state.trim() || null,
        postcode:               postcode.trim() || null,
        public_liability_expiry:       plExpiry || null,
        workers_comp_expiry:           wcExpiry || null,
        professional_indemnity_expiry: piExpiry || null,
        swms_on_file:           swmsOnFile,
        is_approved:            isApproved,
      };

      if (isEdit) {
        const { error } = await supabase
          .from("subcontractor_companies")
          .update(payload)
          .eq("subcontractor_company_id", companyId!);
        if (error) throw error;
        return companyId!;
      }

      const { data, error } = await supabase
        .from("subcontractor_companies")
        .insert({ ...payload, is_active: true })
        .select("subcontractor_company_id")
        .single();
      if (error) throw error;
      return data!.subcontractor_company_id as string;
    },
    onSuccess: () => {
      navigate("/people");
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                 : (err as { message?: string })?.message ?? "Save failed";
      setError(msg);
    },
  });

  if (!hasRole(user, "manager")) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Only managers and directors can add new subcontractor companies.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto pb-16">
      <Link
        to="/people"
        className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2"
      >
        <ArrowLeft size={14} /> Back to People
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEdit ? (legalName || "Edit subcontractor company") : "New subcontractor company"}
          </h1>
          <div className="text-text-muted text-[13px] mt-1">
            The operational subbie companies you engage on jobs (separate from Xero AP
            suppliers). {isEdit ? "Insurance certs are added/renewed on the linked supplier record; approval + suspend live in the Compliance Centre." : "After you save, they'll appear in the Supplier branch of the subbie onboarding wizard."}
          </div>
        </div>
        <Link to="/subbies/compliance">
          <Button variant="secondary" icon={ShieldCheck}>Compliance centre</Button>
        </Link>
      </div>

      {isEdit && loadingExisting && (
        <div className="py-10 flex justify-center"><Spinner /></div>
      )}

      <Card>
        <div className="p-4 md:p-5 grid gap-4">
          {/* Identity */}
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">Identity</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Legal name *">
              <Input
                type="text"
                value={legalName}
                onChange={e => setLegalName(e.target.value)}
                placeholder="ACME Scaffolding Pty Ltd"
              />
            </Field>
            <Field label="Trading name" hint="If different from legal name">
              <Input
                type="text"
                value={tradingName}
                onChange={e => setTradingName(e.target.value)}
                placeholder="ACME Scaffolding"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ABN">
              <Input
                type="text"
                value={abn}
                onChange={e => setAbn(e.target.value)}
                placeholder="11 222 333 444"
                mono
              />
            </Field>
            <Field label="Company code" hint="Short slug — auto if blank">
              <Input
                type="text"
                value={companyCode}
                onChange={e => setCompanyCode(e.target.value.toUpperCase())}
                placeholder={slugifyToCode(tradingName || legalName) || "SUBBIE-CODE"}
                mono
              />
            </Field>
          </div>

          {/* Contact */}
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mt-2">Primary contact</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <Input
                type="text"
                value={contactName}
                onChange={e => setContactName(e.target.value)}
              />
            </Field>
            <Field label="Phone">
              <Input
                type="tel"
                value={contactPhone}
                onChange={e => setContactPhone(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Email">
            <Input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
            />
          </Field>

          {/* Address */}
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mt-2">Address</div>
          <Field label="Line 1">
            <Input type="text" value={addr1} onChange={e => setAddr1(e.target.value)} />
          </Field>
          <Field label="Line 2">
            <Input type="text" value={addr2} onChange={e => setAddr2(e.target.value)} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Suburb">
              <Input type="text" value={suburb} onChange={e => setSuburb(e.target.value)} />
            </Field>
            <Field label="State">
              <select
                value={state}
                onChange={e => setState(e.target.value)}
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
              >
                {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Postcode">
              <Input type="text" value={postcode} onChange={e => setPostcode(e.target.value)} mono />
            </Field>
          </div>

          {/* Insurance */}
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mt-2">Insurance & SWMS</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Public liability expiry">
              <Input type="date" value={plExpiry} onChange={e => setPlExpiry(e.target.value)} />
            </Field>
            <Field label="Workers comp expiry">
              <Input type="date" value={wcExpiry} onChange={e => setWcExpiry(e.target.value)} />
            </Field>
            <Field label="Professional indemnity">
              <Input type="date" value={piExpiry} onChange={e => setPiExpiry(e.target.value)} />
            </Field>
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-[13px]">
            <input
              type="checkbox"
              checked={swmsOnFile}
              onChange={e => setSwmsOnFile(e.target.checked)}
              className="mt-1 accent-accent"
            />
            <span>
              <span className="font-medium">SWMS on file</span>
              <span className="block text-[11px] text-text-muted">
                Tick if a current Safe Work Method Statement has been received and reviewed.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer text-[13px]">
            <input
              type="checkbox"
              checked={isApproved}
              onChange={e => setIsApproved(e.target.checked)}
              className="mt-1 accent-accent"
            />
            <span>
              <span className="font-medium">Approved for engagement</span>
              <span className="block text-[11px] text-text-muted">
                Tick when insurance + SWMS + commercial vetting is complete. Pre-approval
                companies still appear in the wizard with a "pending" warning.
              </span>
            </span>
          </label>
        </div>
      </Card>

      {error && (
        <Card className="mt-4 border-danger/30">
          <div className="p-4 flex items-start gap-3 text-sm text-danger">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
            <span>{error}</span>
          </div>
        </Card>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <Link to="/people">
          <Button variant="ghost" disabled={create.isPending}>Cancel</Button>
        </Link>
        <Button
          variant="primary"
          icon={Building2}
          onClick={() => { setError(null); create.mutate(); }}
          disabled={create.isPending}
        >
          {create.isPending ? "Saving…" : isEdit ? "Save changes" : "Create company"}
        </Button>
      </div>
    </div>
  );
}
